import type { AxisConfiguration } from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import { PPA_CHART_PAGE_LIMIT } from 'constants/ppa';
import type {
    PricePackArchitectureRequest,
    UserInput,
} from 'hooks/ppa/useFetchPricePackArchitecture';

import {
    getAxisConfigurationDefaultsFromMeta,
    getXAxisColumnOptions,
    getXAxisGroupByOptions,
} from './packArchitectureMeta.helpers';
import type { PpaMetaControlNode, PpaMetaOptionItem } from './packArchitectureMeta.types';
import type {
    PpaChartAggregateConfig,
    PpaChartFilters,
    PpaChartTimePeriod,
    PpaChartUserInputs,
} from './pricePackArchitecture.types';

export const appendTableSearchToRequest = (
    request: PricePackArchitectureRequest,
    tableSearchKeyword: string,
): PricePackArchitectureRequest => {
    const keyword = tableSearchKeyword.trim();
    if (!keyword) return request;

    const filteredInputs = (request.user_inputs ?? []).filter((entry) => entry.id !== 'search_keyword');

    return {
        ...request,
        user_inputs: [...filteredInputs, { id: 'search_keyword', input_val: keyword }],
        offset: 0,
    };
};

const SORT_MODE_IDS = new Set<string>([
    'sort_on_alphabet',
    'sort_on_value_sales',
    'sort_on_volume_sales',
]);

const isSortModeId = (id: string) => SORT_MODE_IDS.has(id);

export function isKpiXAxisValue(xAxis: string, xAxisConfig?: PpaMetaControlNode): boolean {
    return getXAxisColumnOptions(xAxisConfig).some((item) => item.id === xAxis);
}

export const buildDimensionAxisUserInputs = (config: AxisConfiguration): PpaChartUserInputs => ({
    sort_by: config.sortBy,
    sort_order: config.sortOrder,
    show_top_n: config.showTop,
    group_rest_as_other: config.groupRestAsOther,
    ...(config.showOwnVendorFirst ? { own_dimension_sort: true } : {}),
});

export const buildDefaultDimensionAxisUserInputs = (
    controlsMeta: AxisGroupByControlsMeta | null | undefined,
): PpaChartUserInputs | undefined => {
    const defaults = getAxisConfigurationDefaultsFromMeta(controlsMeta);
    if (!defaults?.sortBy) return undefined;

    return {
        sort_by: defaults.sortBy,
        sort_order: defaults.sortOrder,
        group_rest_as_other: defaults.groupRestAsOther ?? false,
        ...(defaults.showOwnVendorFirst ? { own_dimension_sort: true } : {}),
    };
};

export const buildKpiAxisUserInputs = (config: KpiAxisConfiguration): PpaChartUserInputs => ({
    bin_min: config.min,
    bin_max: config.max,
    bin_method: config.binningMethod,
    ...(config.binningMethod === 'uniform'
        ? { bin_count: config.binCount }
        : { breakpoints: config.breakpoints }),
});

export const buildXAxisUserInputs = (
    xAxis: string,
    xAxisConfig?: PpaMetaControlNode,
    dimensionConfig?: AxisConfiguration,
    kpiConfig?: KpiAxisConfiguration,
    groupByControlsMeta?: AxisGroupByControlsMeta | null,
): PpaChartUserInputs | undefined => {
    if (isKpiXAxisValue(xAxis, xAxisConfig)) {
        return kpiConfig ? buildKpiAxisUserInputs(kpiConfig) : undefined;
    }
    if (dimensionConfig) {
        return buildDimensionAxisUserInputs(dimensionConfig);
    }
    return buildDefaultDimensionAxisUserInputs(groupByControlsMeta);
};

const pushUserInput = (entries: UserInput[], id: string, value: string | number | boolean) => {
    entries.push({ id, input_val: String(value) });
};

const serializeDimensionAxisUserInputs = (
    entries: UserInput[],
    inputs: PpaChartUserInputs,
) => {
    if (inputs.sort_by) {
        pushUserInput(entries, 'sort_by', inputs.sort_by);
        if (inputs.sort_order && isSortModeId(inputs.sort_by)) {
            pushUserInput(entries, inputs.sort_by, inputs.sort_order);
        }
    }
    if (typeof inputs.show_top_n === 'number' && Number.isFinite(inputs.show_top_n)) {
        pushUserInput(entries, 'show_top_n', inputs.show_top_n);
    }
    if (typeof inputs.group_rest_as_other === 'boolean') {
        pushUserInput(entries, 'consolidate_as_others', inputs.group_rest_as_other);
    }
    if (inputs.own_dimension_sort) {
        pushUserInput(entries, 'own_dimension_sort', true);
    }
};

const serializeKpiAxisUserInputs = (entries: UserInput[], inputs: PpaChartUserInputs) => {
    if (typeof inputs.bin_min === 'number') pushUserInput(entries, 'min', inputs.bin_min);
    if (typeof inputs.bin_max === 'number') pushUserInput(entries, 'max', inputs.bin_max);
    if (inputs.bin_method) pushUserInput(entries, 'bin_method', inputs.bin_method);
    if (typeof inputs.bin_count === 'number') pushUserInput(entries, 'count', inputs.bin_count);
    inputs.breakpoints?.forEach((breakpoint) => pushUserInput(entries, 'break_point', breakpoint));

    const searchKeyword = inputs.search_keyword?.trim();
    if (searchKeyword) {
        pushUserInput(entries, 'search_keyword', searchKeyword);
    }
};

/** Maps PA-07 axis configuration to the API `user_inputs` array shape. */
export const serializePpaChartUserInputs = (inputs?: PpaChartUserInputs): UserInput[] | undefined => {
    if (!inputs) return undefined;

    const entries: UserInput[] = [];
    serializeDimensionAxisUserInputs(entries, inputs);
    serializeKpiAxisUserInputs(entries, inputs);

    return entries.length > 0 ? entries : undefined;
};

export const getShowTopFromUserInputs = (userInputs?: UserInput[]): number | undefined => {
    const showTopInput = userInputs?.find((entry) => entry.id === 'show_top_n');
    if (!showTopInput) return undefined;
    const parsed = Number(showTopInput.input_val);
    return Number.isFinite(parsed) ? parsed : undefined;
};

/** Caps chart/table row budget when show_top_n is applied (API total_count may still be higher). */
export const getEffectiveChartRowCount = (
    userInputs: UserInput[] | undefined,
    totalCount: number,
): number => {
    const showTop = getShowTopFromUserInputs(userInputs);
    if (showTop === undefined) return totalCount;
    return Math.min(totalCount, showTop);
};

export const getChartPageLimit = (userInputs: UserInput[] | undefined, offset: number): number => {
    const showTop = getShowTopFromUserInputs(userInputs);
    if (showTop !== undefined) {
        const remaining = showTop - offset;
        if (remaining <= 0) return 0;
        return Math.min(remaining, PPA_CHART_PAGE_LIMIT);
    }
    return PPA_CHART_PAGE_LIMIT;
};

const AXIS_PREVIEW_USER_INPUT_IDS = new Set([
    'sort_by',
    'sort_order',
    ...SORT_MODE_IDS,
    'show_top_n',
    'consolidate_as_others',
    'group_rest_as_other',
    'own_dimension_sort',
]);

/** Builds a one-shot preview request with draft axis settings without affecting chart pagination. */
export const buildAxisPreviewRequest = (
    chartRequest: PricePackArchitectureRequest,
    axisConfig: AxisConfiguration,
): PricePackArchitectureRequest => {
    const filteredInputs = (chartRequest.user_inputs ?? []).filter(
        (entry) => !AXIS_PREVIEW_USER_INPUT_IDS.has(entry.id),
    );
    const axisUserInputs =
        serializePpaChartUserInputs(buildDimensionAxisUserInputs(axisConfig)) ?? [];
    const fetchLimit = axisConfig.showTop > 0 ? axisConfig.showTop : PPA_CHART_PAGE_LIMIT;

    return {
        ...chartRequest,
        user_inputs: [...filteredInputs, ...axisUserInputs],
        limit: fetchLimit,
        offset: 0,
    };
};

const resolveMetaIsAttribute = (item: PpaMetaOptionItem): boolean | null =>
    item.is_attribute === undefined ? null : item.is_attribute;

const resolveMetaDimension = (item: PpaMetaOptionItem): string | null =>
    item.dimension === undefined ? null : item.dimension;

const toAggregate = (
    item: PpaMetaOptionItem,
    fallbackId: string,
    isBubble: boolean,
): PpaChartAggregateConfig => ({
    dimension: resolveMetaDimension(item),
    attribute_type: item.id ?? fallbackId,
    is_attribute: resolveMetaIsAttribute(item),
    is_bubble: isBubble,
});

type BuildChartRequestParams = {
    xAxis: string;
    yAxis: string;
    bubbleRepresents: string;
    bubbleSize: string;
    xAxisConfig?: PpaMetaControlNode;
    yAxisConfig?: PpaMetaControlNode;
    bubbleRepresentsConfig?: PpaMetaControlNode;
    bubbleSizeConfig?: PpaMetaControlNode;
    xAxisGroupByControlsMeta?: AxisGroupByControlsMeta | null;
    timePeriod: PpaChartTimePeriod;
    filters?: PpaChartFilters;
    dimensionAxisConfig?: AxisConfiguration;
    kpiAxisConfig?: KpiAxisConfiguration;
    tableKpiIds?: string[];
    offset?: number;
};

const buildXAxisAggregate = (
    xAxis: string,
    xAxisDimensionItem: PpaMetaOptionItem | undefined,
    xAxisKpiItem: PpaMetaOptionItem | undefined,
    preferKpiItem = false,
): PpaChartAggregateConfig | undefined => {
    if (preferKpiItem && xAxisKpiItem) {
        return toAggregate(xAxisKpiItem, xAxis, false);
    }
    if (xAxisDimensionItem) {
        return toAggregate(xAxisDimensionItem, xAxis, false);
    }
    if (xAxisKpiItem) {
        return toAggregate(xAxisKpiItem, xAxis, false);
    }
    return undefined;
};

const buildBubbleRepresentsAggregate = (
    bubbleRepresents: string,
    bubbleRepresentsItem: PpaMetaOptionItem | undefined,
): PpaChartAggregateConfig => {
    if (bubbleRepresentsItem) {
        return toAggregate(bubbleRepresentsItem, bubbleRepresents, true);
    }
    return { dimension: 'dim1', attribute_type: bubbleRepresents, is_attribute: null, is_bubble: true };
};

const buildChartKpiIds = (
    yAxisId: string,
    bubbleSizeId: string,
    tableKpiIds: string[] = [],
): Array<{ id: string; is_bubble_size: boolean }> => {
    const entries: Array<{ id: string; is_bubble_size: boolean }> = [];

    const hasEntry = (id: string, isBubbleSize: boolean) =>
        entries.some((entry) => entry.id === id && entry.is_bubble_size === isBubbleSize);

    const push = (id: string | undefined, isBubbleSize: boolean) => {
        if (!id || hasEntry(id, isBubbleSize)) return;
        entries.push({ id, is_bubble_size: isBubbleSize });
    };

    push(yAxisId, false);
    push(bubbleSizeId, true);

    tableKpiIds.forEach((id) => {
        if (!id || id === yAxisId) return;
        push(id, false);
    });

    return entries;
};

export const buildPpaChartRequest = ({
    xAxis,
    yAxis,
    bubbleRepresents,
    bubbleSize,
    xAxisConfig,
    yAxisConfig,
    bubbleRepresentsConfig,
    bubbleSizeConfig,
    xAxisGroupByControlsMeta,
    timePeriod,
    filters,
    dimensionAxisConfig,
    kpiAxisConfig,
    tableKpiIds = [],
    offset = 0,
}: BuildChartRequestParams): PricePackArchitectureRequest | null => {
    if (!xAxis || !yAxis || !bubbleRepresents || !bubbleSize) return null;
    if (!timePeriod?.start_timestamp || !timePeriod?.end_timestamp) return null;

    const xAxisGroupByOptions = getXAxisGroupByOptions(xAxisConfig);
    const xAxisColumnOptions = getXAxisColumnOptions(xAxisConfig);

    const xAxisDimensionItem = xAxisGroupByOptions.find((item) => item.id === xAxis);
    const xAxisKpiItem = xAxisColumnOptions.find((item) => item.id === xAxis);
    const isKpiXAxis = isKpiXAxisValue(xAxis, xAxisConfig);
    const bubbleRepresentsItem = (bubbleRepresentsConfig?.group_by_options ?? []).find(
        (item) => item.id === bubbleRepresents,
    );
    const yAxisItem = (yAxisConfig?.column_options ?? []).find((item) => item.id === yAxis);
    const bubbleSizeItem = (bubbleSizeConfig?.column_options ?? []).find((item) => item.id === bubbleSize);

    const xAxisAggregate = buildXAxisAggregate(
        xAxis,
        xAxisDimensionItem,
        xAxisKpiItem,
        isKpiXAxis,
    );
    const aggregates: PpaChartAggregateConfig[] = [
        ...(xAxisAggregate ? [xAxisAggregate] : []),
        buildBubbleRepresentsAggregate(bubbleRepresents, bubbleRepresentsItem),
    ];

    const kpiIds = buildChartKpiIds(
        yAxisItem?.id ?? yAxis,
        bubbleSizeItem?.id ?? bubbleSize,
        tableKpiIds,
    );
    const rawUserInputs = buildXAxisUserInputs(
        xAxis,
        xAxisConfig,
        dimensionAxisConfig,
        kpiAxisConfig,
        xAxisGroupByControlsMeta,
    );
    const userInputs = serializePpaChartUserInputs(rawUserInputs);
    const limit = getChartPageLimit(userInputs, offset);

    if (limit <= 0) return null;

    return {
        kpi_ids: kpiIds,
        time_period: timePeriod,
        filters,
        aggregates,
        user_inputs: userInputs,
        limit,
        offset,
    };
};
